import { useEffect, useState, useSyncExternalStore, useTransition } from 'react';
import { useBrowserLocation } from 'wouter/use-browser-location';
import type { BrowserLocationHook } from 'wouter/use-browser-location';

/**
 * Transition-aware location hook for wouter's <Router hook={...}>.
 *
 * wouter tracks the browser location via useSyncExternalStore, and uSES
 * updates are always urgent — wrapping navigate() in startTransition has no
 * transition semantics. This bridge keeps wouter's own browser-location hook
 * (navigate / popstate / pushState monkey-patch all untouched) and mirrors
 * only the exposed path into React state, updated inside startTransition.
 * While a lazy route chunk suspends, the Switch keeps matching the previous
 * displayPath, so the old page stays on screen instead of dropping to a
 * fallback (see router.tsx for the single persistent Suspense boundary this
 * relies on).
 *
 * Known-accepted asymmetry: useSearch/useSearchParams read the router's
 * searchHook, which stays on the urgent browser hook — search params update
 * immediately while the path defers. No current page reads both during a
 * path transition, so the skew is unobservable today.
 *
 * wouter invokes router.hook() in every location consumer (Route, Switch,
 * Link, useLocation), so this hook runs as many parallel instances. React
 * entangles transition lanes started in the same flush, so every instance's
 * displayPath commits in the same render — consumers never tear.
 */

// Module-level pending signal shared by all hook instances (see note above on
// instance multiplicity): counts instances with an in-flight transition and
// fans out through useSyncExternalStore.
let pendingInstances = 0;
const listeners = new Set<() => void>();
const notifyPending = () => listeners.forEach(listener => listener());
const subscribePending = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
const getPendingSnapshot = () => pendingInstances > 0;

/** True while a route transition is pending (target chunk still loading). */
export const useRouteTransitionPending = () => useSyncExternalStore(subscribePending, getPendingSnapshot, () => false);

export const useTransitionLocation: BrowserLocationHook = opts => {
  const [location, navigate] = useBrowserLocation(opts);
  const [displayPath, setDisplayPath] = useState(location);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    if (location === displayPath) return;
    startTransition(() => setDisplayPath(location));
  }, [location, displayPath, startTransition]);

  useEffect(() => {
    if (!isPending) return;
    pendingInstances++;
    notifyPending();
    return () => {
      pendingInstances--;
      notifyPending();
    };
  }, [isPending]);

  return [displayPath, navigate];
};
